export const REFERRAL_STORAGE_KEY = 'imobilify_referral_code';
export const REFERRAL_COOKIE_NAME = 'imobilify_ref';
export const REFERRAL_EXPIRATION_DAYS = 60;

/**
 * Sanitiza o código de indicação removendo qualquer caractere suspeito (XSS / SQLi prevention)
 * Aceita apenas letras, números, hífen e underscore. Limita tamanho a 50 caracteres.
 */
export function sanitizeReferralCode(rawCode: string | null | undefined): string | null {
  if (!rawCode || typeof rawCode !== 'string') return null;

  const trimmed = rawCode.trim();
  if (!trimmed) return null;

  // Manter apenas caracteres alfanuméricos, hífens e underscores
  const cleanCode = trimmed.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 50);
  return cleanCode.length > 0 ? cleanCode : null;
}

/**
 * Obtém o domínio pai para compartilhamento de cookies entre subdomínios (ex: .imobpanda.com)
 */
function getCookieDomain(): string {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname;

  if (hostname.endsWith('imobpanda.com')) {
    return '; domain=.imobpanda.com';
  }
  if (hostname.endsWith('imobpanda.site')) {
    return '; domain=.imobpanda.site';
  }
  return ''; // Localhost ou IP sem domínio configurado
}

/**
 * Salva o código de indicação no Cookie de 1º nível e no localStorage
 */
export function setStoredReferralCode(code: string): boolean {
  const cleanCode = sanitizeReferralCode(code);
  if (!cleanCode) return false;

  if (typeof window === 'undefined') return false;

  try {
    // 1. Gravar em localStorage
    localStorage.setItem(REFERRAL_STORAGE_KEY, cleanCode);

    // 2. Gravar em Cookie com TTL de 60 dias e wildcard de domínio
    const expiresDate = new Date();
    expiresDate.setDate(expiresDate.getDate() + REFERRAL_EXPIRATION_DAYS);

    const isSecure = window.location.protocol === 'https:' ? '; Secure' : '';
    const domainAttr = getCookieDomain();
    
    document.cookie = `${REFERRAL_COOKIE_NAME}=${encodeURIComponent(
      cleanCode
    )}; expires=${expiresDate.toUTCString()}; path=/${domainAttr}${isSecure}; SameSite=Lax`;

    return true;
  } catch (error) {
    console.error('[referralStorage] Erro ao armazenar código de indicação:', error);
    return false;
  }
}

/**
 * Obtém o código de indicação armazenado buscando primeiro na URL (se passado),
 * depois em Cookies (cross-subdomain) e por último em localStorage.
 */
export function getStoredReferralCode(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    // 1. Tentar ler do Cookie (suporta navegação cross-subdomain)
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === REFERRAL_COOKIE_NAME && value) {
        const decoded = decodeURIComponent(value);
        const cleanCookieCode = sanitizeReferralCode(decoded);
        if (cleanCookieCode) return cleanCookieCode;
      }
    }

    // 2. Fallback para localStorage
    const localCode = localStorage.getItem(REFERRAL_STORAGE_KEY);
    const cleanLocalCode = sanitizeReferralCode(localCode);
    if (cleanLocalCode) return cleanLocalCode;
  } catch (error) {
    console.error('[referralStorage] Erro ao ler código de indicação:', error);
  }

  return null;
}

/**
 * Remove o código de indicação de todas as camadas de armazenamento
 */
export function clearStoredReferralCode(): void {
  if (typeof window === 'undefined') return;

  try {
    // Limpar localStorage
    localStorage.removeItem(REFERRAL_STORAGE_KEY);

    // Limpar Cookie
    const domainAttr = getCookieDomain();
    document.cookie = `${REFERRAL_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttr}`;
  } catch (error) {
    console.error('[referralStorage] Erro ao limpar código de indicação:', error);
  }
}
